/**
 * Item da lista de conversas (avatar, preview, tags, unread).
 */
import React from 'react';
import { AppIcon, type IconName } from '../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { ChatConversation } from '../hooks/useChat';
import { CHAT_DELETED_LABEL } from '../hooks/types';
import { ChatAvatar } from './chat-window/ChatAvatar';

interface Props {
    conversation: ChatConversation;
    isActive: boolean;
    onClick: (id: string, connectionId?: string | null) => void;
    stageName?: string | null;
    assignedUserName?: string | null;
    assignedUserId?: string | null;
    isFavorite?: boolean;
}

const PREVIEW_ICONS: Record<string, IconName> = {
    '📷': 'image', '🎥': 'video', '🎬': 'video', '🎤': 'mic', '🎵': 'mic',
    '📄': 'file-text', '💟': 'smile', '🎭': 'smile', '📍': 'location', '👤': 'user', '📊': 'chart',
    '💳': 'credit-card',
};

const JSON_PREVIEW: Array<[string, string]> = [
    ['"degreesLatitude"', '📍 Localização'], ['"vcard"', '👤 Contato'],
    ['"selectableOptionsCount"', '📊 Enquete'], ['"fileName"', '📄 Documento'],
    ['"pixKey"', '💳 PIX'], ['"pixType"', '💳 PIX'],
];

const SEMANTIC_BADGES = [
    'badge-success',
    'badge-brand-dark',
    'badge-brand',
    'badge-info',
    'badge-warning',
    'badge-danger',
] as const;

type SemanticBadge = (typeof SEMANTIC_BADGES)[number];

const nextSemanticBadge = (store: Map<string, SemanticBadge>, key?: string | null) => {
    if (!key) return 'badge-neutral';
    const cached = store.get(key);
    if (cached) return cached;
    const color = SEMANTIC_BADGES[store.size % SEMANTIC_BADGES.length];
    store.set(key, color);
    return color;
};

const assigneeColorByKey = new Map<string, SemanticBadge>();
const stageColorByKey = new Map<string, SemanticBadge>();

const toTimeMs = (date?: Date | string | null) => {
    if (!date) return null;
    const ms = (typeof date === 'string' ? new Date(date) : date).getTime();
    return Number.isNaN(ms) ? null : ms;
};

const formatPreview = (text?: string) => {
    if (!text) return 'Nova conversa';
    const clean = text.replace('🚫 ', '');
    if (!clean.startsWith('{') && !clean.startsWith('[')) return clean;
    return JSON_PREVIEW.find(([needle]) => clean.includes(needle))?.[1] ?? clean;
};

function PreviewIconLine({ icon, text, italic }: { icon: IconName; text: string; italic?: boolean }) {
    return (
        <>
            <AppIcon name={icon} className="size-3 shrink-0 text-muted" aria-hidden />
            <span className={cn('truncate', italic && 'italic')}>{text}</span>
        </>
    );
}

function PreviewText({ text }: { text?: string }) {
    const preview = formatPreview(text);
    if (preview.includes(CHAT_DELETED_LABEL)) {
        return <PreviewIconLine icon="ban" text={preview} italic />;
    }
    const chars = [...preview];
    const icon = PREVIEW_ICONS[chars[0] ?? ''];
    if (icon) {
        return <PreviewIconLine icon={icon} text={chars.slice(1).join('').trim()} />;
    }
    return (
        <span className="truncate">
            {preview.split(/(\*[^*]+\*)/g).map((part, i) =>
                part.startsWith('*') && part.endsWith('*') && part.length > 2
                    ? <strong key={i}>{part.slice(1, -1)}</strong>
                    : <React.Fragment key={i}>{part}</React.Fragment>
            )}
        </span>
    );
}

function RowBadge({ text, title, className }: { text: string; title?: string; className?: string }) {
    return (
        <span className={cn('badge shrink-0 truncate font-semibold', className)} title={title ?? text}>
            {text}
        </span>
    );
}

const propsEqual = (p: Props, n: Props) => {
    const a = p.conversation;
    const b = n.conversation;
    return (
        p.isActive === n.isActive && p.stageName === n.stageName &&
        p.assignedUserName === n.assignedUserName && p.assignedUserId === n.assignedUserId &&
        p.isFavorite === n.isFavorite &&
        a.id === b.id && a.name === b.name && a.lastMessage === b.lastMessage && a.unreadCount === b.unreadCount &&
        a.assignedTo === b.assignedTo && a.avatar === b.avatar &&
        toTimeMs(a.lastMessageTime) === toTimeMs(b.lastMessageTime)
    );
};

const ConversationItem = React.memo(({
    conversation, isActive, onClick, stageName,
    assignedUserName, assignedUserId, isFavorite,
}: Props) => {
    const unread = conversation.unreadCount;
    const hasUnread = unread > 0;
    const lastMs = toTimeMs(conversation.lastMessageTime);

    return (
        <button
            type="button"
            aria-current={isActive ? 'true' : undefined}
            aria-label={`Conversa com ${conversation.name}${hasUnread ? `, ${unread} não lidas` : ''}`}
            onClick={() => onClick(conversation.id, conversation.connectionId)}
            className={cn(
                'conv-row flex w-full min-w-0 appearance-none items-center gap-3 border-0',
                'bg-transparent px-s3 py-s2 text-left shadow-none outline-none touch-manipulation md:py-s3',
                'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/25',
                isActive && 'conv-row--active',
                hasUnread && 'conv-row--unread',
            )}
        >
            <div className="relative shrink-0">
                <ChatAvatar
                    name={conversation.name}
                    avatar={conversation.avatar}
                    size="lg"
                    customBadgeIcon={conversation.platform === 'instagram' ? 'instagram' : 'whatsapp'}
                />
                {hasUnread ? (
                    <span
                        className={cn(
                            'absolute -right-0.5 -top-0.5 z-2 inline-flex min-h-[17px] min-w-[17px]',
                            'items-center justify-center rounded-full border-2 border-card',
                            'bg-danger-500 px-1 font-mono text-2xs font-semibold leading-none',
                            'text-destructive-foreground tabular-nums shadow-xs',
                        )}
                        aria-hidden="true"
                    >
                        {unread > 99 ? '99+' : unread}
                    </span>
                ) : null}
            </div>

            <span className="min-w-0 flex-1">
                <span className="mb-1 flex items-baseline justify-between gap-2">
                    <span className="flex min-w-0 flex-1 items-center gap-1">
                        <span className="conv-name truncate">{conversation.name}</span>
                        {isFavorite && (
                            <AppIcon name="star-filled" className="size-3 shrink-0 text-yellow-400" aria-hidden />
                        )}
                    </span>
                    <time
                        className={cn('conv-time shrink-0', hasUnread && 'conv-time--unread')}
                        dateTime={lastMs != null ? new Date(lastMs).toISOString() : undefined}
                    >
                        {lastMs == null ? '' : new Date(lastMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </time>
                </span>
                <span className="flex min-w-0 items-center gap-2">
                    <span className="conv-preview flex min-w-0 flex-1 items-center gap-1">
                        <PreviewText text={conversation.lastMessage} />
                    </span>
                    {(assignedUserName || stageName) && (
                        <span className="conv-meta">
                            {assignedUserName && (
                                <RowBadge
                                    text={assignedUserName.split(' ')[0]}
                                    title={assignedUserName}
                                    className={nextSemanticBadge(assigneeColorByKey, assignedUserId ?? assignedUserName)}
                                />
                            )}
                            {stageName && (
                                <RowBadge
                                    text={stageName}
                                    className={cn('max-w-[9rem]', nextSemanticBadge(stageColorByKey, stageName))}
                                />
                            )}
                        </span>
                    )}
                </span>
            </span>
        </button>
    );
}, propsEqual);

export default ConversationItem;
